// src/components/property/PropertyCard.tsx
/**
 * PropertyCard
 *
 * Displays a property listing card with thumbnail, title, price, city, and status.
 * Supports selection mode (checkbox) and individual delete (three-dot menu).
 */

import { memo, useState } from 'react';
import { MapPin, MoreVertical, Trash2 } from 'lucide-react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { PropertyStatusBadgeMemo } from './PropertyStatusBadge';
import { DeletePropertyDialog } from './DeletePropertyDialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { IPropertyCardProps } from '@/@types/property';

const PropertyCard = ({
  property,
  onClick,
  isSelectionMode = false,
  isSelected = false,
  onSelectToggle,
}: IPropertyCardProps) => {
  // Delete state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteListing = useMutation(api.property.mutations.deletePropertyListing);
  // Get first image URL if available
  const firstImageId = property.imageIds[0];
  const imageUrl = useQuery(
    api.storage.getUrl,
    firstImageId ? { storageId: firstImageId } : 'skip'
  );

  const formatPrice = (price?: number, currency?: string): string | null => {
    if (!price) return null;
    const currencySymbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency || '€';
    return `${currencySymbol}${price.toLocaleString()}`;
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteListing({ propertyId: property._id });
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete property:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCheckboxChange = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectToggle?.(property._id);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() =>
        !isSelectionMode && !isDeleteDialogOpen && !isDeleting && onClick(property._id)
      }
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (!isSelectionMode && !isDeleteDialogOpen && !isDeleting) {
            onClick(property._id);
          }
        }
      }}
      className={`group bg-card rounded-xl border overflow-hidden hover:shadow-md cursor-pointer transition-all duration-200 relative ${
        isSelected ? 'border-ring ring-2 ring-ring/20' : 'border-border hover:border-border-hover'
      }`}
    >
      {/* Selection Checkbox (shown in selection mode) */}
      {isSelectionMode && (
        <div
          role="button"
          tabIndex={0}
          className="absolute top-3 left-3 z-10"
          onClick={handleCheckboxChange}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onSelectToggle?.(property._id);
            }
          }}
        >
          <Checkbox checked={isSelected} className="h-5 w-5 bg-card border-2" />
        </div>
      )}

      {/* Three-dot Menu (always visible) */}
      {!isSelectionMode && (
        <div className="absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 bg-card/90 hover:bg-card shadow-sm"
                onClick={e => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem variant="destructive" onClick={handleDeleteClick}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Thumbnail */}
      <div className="aspect-[16/10] bg-muted overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={property.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <MapPin className="h-12 w-12" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Price */}
        {property.price && (
          <div className="text-lg font-bold text-card-foreground mb-1">
            {formatPrice(property.price, property.currency)}
          </div>
        )}

        {/* Title */}
        <h3 className="text-sm font-medium text-card-foreground line-clamp-2 mb-2">
          {property.title}
        </h3>

        {/* City */}
        {property.city && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
            <MapPin className="h-3.5 w-3.5" />
            <span>{property.city}</span>
          </div>
        )}

        {/* Status Badge */}
        <PropertyStatusBadgeMemo
          propertyStatus={property.status}
          videoStatus={property.videoStatus}
          errorMessage={property.errorMessage}
        />
      </div>

      {/* Delete Confirmation Dialog - wrapped to prevent click propagation */}
      {isDeleteDialogOpen && (
        <div
          role="presentation"
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >
          <DeletePropertyDialog
            isOpen={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            onConfirm={handleDeleteConfirm}
            isDeleting={isDeleting}
            propertyTitle={property.title}
          />
        </div>
      )}
    </div>
  );
};

export const PropertyCardMemo = memo(PropertyCard);
